import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const PROFILE_ROUTE_PREFIXES = new Set([
  "userhome",
  "edit-user",
  "work-history",
  "edit-workhistory",
  "healthcard",
  "edit-healthcard",
  "fastcard",
  "edit-fastcard",
  "fuelcard",
  "edit-fuelcard",
  "workpermit",
  "edit-workpermit",
  "driverabstract",
  "roadtest",
  "roadside",
  "driverlicense",
  "edit-driver",
  "vacationform",
  "edit-vacation",
  "emergencycontact",
  "edit-emergency",
  "canadapost",
  "edit-canadapost",
  "incidents",
  "violations",
  "annual-driver-certifications",
  "driver-disclosure",
  "directdeposit",
  "edit-directdeposit",
  "dangerouswork",
  "zerotolerance",
  "truckassignment",
  "healthandsafety",
  "padlockpolicy",
  "background-check-fcra",
  "qualifications",
  "driver-agreement",
  "gatepasspolicy",
  "resume",
  "leep",
  "consent",
  "signed-release",
  "tags",
  "training",
  "interview",
  "clearinghouse",
  "status-history",
  "application-history",
]);

const getProfileUserId = (pathname) => {
  const segments = pathname.split("/").filter(Boolean);
  let candidate = null;

  if (segments[0] === "task") {
    candidate = segments[2];
  } else if (segments[0] === "edit-task") {
    candidate = segments[2];
  } else if (PROFILE_ROUTE_PREFIXES.has(segments[0])) {
    candidate = segments[1];
  }

  return /^\d+$/.test(candidate || "") ? candidate : null;
};

const parseCategoryIds = (value) => {
  if (Array.isArray(value)) return value.map(String);
  if (value == null || value === "") return [];

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map(String);
  } catch {
    // Fall through to comma-separated or scalar values.
  }

  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

function UserProfileContextBanner({
  sidebarOpen = false,
  onOpenActionCentre,
  refreshKey = 0,
}) {
  const location = useLocation();
  const profileUserId = useMemo(
    () => getProfileUserId(location.pathname),
    [location.pathname],
  );
  const [profile, setProfile] = useState(null);
  const bannerRef = useRef(null);

  useEffect(() => {
    const root = document.documentElement;
    const updateHeight = () => {
      root.style.setProperty(
        "--user-profile-context-height",
        `${bannerRef.current?.offsetHeight || 0}px`,
      );
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    if (bannerRef.current) observer.observe(bannerRef.current);
    window.addEventListener("resize", updateHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateHeight);
      root.style.setProperty("--user-profile-context-height", "0px");
    };
  }, [profile]);

  useEffect(() => {
    if (!profileUserId) {
      setProfile(null);
      return undefined;
    }

    let isCurrent = true;
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };
    const apiBase = process.env.REACT_APP_API_BASE_URL;

    Promise.all([
      axios.get(`${apiBase}/api/users/${profileUserId}`, { headers }),
      axios.get(`${apiBase}/api/dropdowns/user-status`, { headers }),
      axios.get(`${apiBase}/api/dropdowns/categories`, { headers }),
    ])
      .then(([userResponse, statusResponse, categoryResponse]) => {
        if (!isCurrent) return;

        const user = userResponse.data || {};
        const statuses = statusResponse.data || [];
        const categories = categoryResponse.data || [];
        const fullName =
          user.full_name ||
          `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
          `User ${profileUserId}`;
        const employeeNumber = String(user.userid_no ?? "").trim();
        const status =
          statuses.find(
            (item) => String(item.s_id) === String(user.status),
          )?.s_TranslatedText || "Unknown Status";
        const categoryIds = parseCategoryIds(user.emp_category);
        const category =
          categoryIds
            .map(
              (id) =>
                categories.find((item) => String(item.c_id) === id)
                  ?.c_translatedText ||
                categories.find((item) => String(item.c_id) === id)?.c_name,
            )
            .filter(Boolean)
            .join(", ") || "Uncategorized";

        setProfile({
          ...user,
          id: user.id || Number(profileUserId),
          employeeNumber,
          fullName,
          status,
          category,
        });
      })
      .catch((error) => {
        if (!isCurrent) return;
        console.error("Failed to load user profile context", error);
        setProfile(null);
      });

    return () => {
      isCurrent = false;
    };
  }, [profileUserId, refreshKey]);

  if (!profile) return null;

  return (
    <>
      <div
        ref={bannerRef}
        className={`user-profile-context-banner ${
          sidebarOpen ? "with-sidebar" : ""
        }`}
        aria-label="Current user profile"
      >
        <span className="user-profile-context-text">
          {profile.employeeNumber && `${profile.employeeNumber} - `}
          {profile.fullName} - {profile.status} - {profile.category}
        </span>
        <button
          type="button"
          className="user-profile-action-centre"
          onClick={() => onOpenActionCentre?.(profile)}
        >
          Action Centre
        </button>
      </div>
    </>
  );
}

export default UserProfileContextBanner;
